import { useState } from "react";

export const C = { cyan: "#2DD4BF", purple: "#3B82F6", blue: "#2563EB", red: "#F43F5E", yellow: "#F59E0B", green: "#10B981", grey: "#64748b" };
export const REGION_COLORS = { Qatar: C.cyan, India: C.yellow, "": C.grey };
export const ACCENT = "#3B82F6";

export const fmtN = (n, d = 1) => (n == null || isNaN(n) ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }));
export const fmtPct = (n) => (n == null || isNaN(n) ? "—" : `${Number(n).toFixed(1)}%`);
export const fmtInt = (n) => (n == null || isNaN(n) ? "—" : Math.round(n).toLocaleString());
export const money = (n) => (n == null || isNaN(n) ? "—" : Math.round(n).toLocaleString());
export const monthLabel = (m) => {
  if (!m) return "—";
  const [y, mm] = m.split("-");
  return new Date(Number(y), Number(mm) - 1, 1).toLocaleString("en", { month: "long", year: "numeric" });
};
export const attColor = (att) => (att == null ? C.grey : att >= 100 ? C.green : att >= 80 ? C.yellow : C.red);
export const penColor = (p) => (p == null ? C.grey : p >= 5 ? C.red : p > 0 ? C.yellow : C.green);

export function agentSetOf(data, { region }) {
  return new Set(Object.keys(data.agents).filter((u) => region === "All" || data.agents[u].region === region));
}

export function kpisFor(data, month, set) {
  const S = data.sales.filter((s) => s.d.startsWith(month) && set.has(s.u));
  const T = data.targets.filter((t) => t.m === month && set.has(t.u));
  const Ch = data.churn.filter((c) => c.m === month && set.has(c.u));
  const Co = data.commission.filter((c) => c.m === month && set.has(c.u));
  const n = S.reduce((a, s) => a + s.n, 0);
  const target = T.reduce((a, t) => a + t.t, 0);
  const tset = new Set(T.map((t) => t.u)), tp = new Set(data.target_products);
  const nT = S.filter((s) => tset.has(s.u) && tp.has(s.p)).reduce((a, s) => a + s.n, 0);
  const churned = Ch.reduce((a, c) => a + c.n, 0);
  const remaining = data.wd[month]?.remaining || 0;
  const deficit = Math.max(target - nT, 0);
  const sum = (k) => Co.reduce((a, c) => a + (c[k] || 0), 0);
  const penRows = Co.filter((c) => c.pen != null);
  return {
    n, nTargeted: nT, nUntargeted: n - nT, target, att: target ? (100 * nT) / target : null, deficit,
    req: remaining > 0 && deficit > 0 ? deficit / remaining : remaining > 0 ? 0 : null,
    churned, churnPct: n + churned ? (100 * churned) / (n + churned) : null,
    agentsTargeted: tset.size, agentsSelling: new Set(S.map((s) => s.u)).size,
    core: sum("core"), penAmt: sum("pen_amt"), net: sum("net"), share: sum("share"),
    pen: penRows.length ? penRows.reduce((a, c) => a + c.pen, 0) / penRows.length : null, commAgents: Co.length,
  };
}

export function productTable(data, month, set) {
  const by = {};
  data.products.forEach((p) => { by[p] = { p, t: 0, ach: 0, achAll: 0, churned: 0 }; });
  const tset = new Set(data.targets.filter((t) => t.m === month && set.has(t.u)).map((t) => t.u));
  data.targets.forEach((t) => { if (t.m === month && set.has(t.u) && by[t.p]) by[t.p].t += t.t; });
  data.sales.forEach((s) => {
    if (s.d.startsWith(month) && set.has(s.u) && by[s.p]) { by[s.p].achAll += s.n; if (tset.has(s.u)) by[s.p].ach += s.n; }
  });
  data.churn.forEach((c) => { if (c.m === month && set.has(c.u) && by[c.p]) by[c.p].churned += c.n; });
  const remaining = data.wd[month]?.remaining || 0;
  return Object.values(by).filter((r) => r.t || r.achAll).map((r) => ({
    ...r, ach: r.t ? r.ach : r.achAll, att: r.t ? (100 * r.ach) / r.t : null, deficit: r.t ? Math.max(r.t - r.ach, 0) : 0,
    req: remaining > 0 && r.t ? Math.max(r.t - r.ach, 0) / remaining : null,
    churnPct: r.achAll + r.churned ? (100 * r.churned) / (r.achAll + r.churned) : null,
  }));
}

export function agentRows(data, month, set) {
  const by = {};
  const tp = new Set(data.target_products);
  const g = (u) => (by[u] = by[u] || { n: 0, nTP: 0, t: 0, churned: 0, core: 0, penSum: 0, penN: 0, penAmt: 0, net: 0, share: 0, hasComm: false });
  data.sales.forEach((s) => { if (s.d.startsWith(month) && set.has(s.u)) { const b = g(s.u); b.n += s.n; if (tp.has(s.p)) b.nTP += s.n; } });
  data.targets.forEach((t) => { if (t.m === month && set.has(t.u)) g(t.u).t += t.t; });
  data.churn.forEach((c) => { if (c.m === month && set.has(c.u)) g(c.u).churned += c.n; });
  data.commission.forEach((c) => { if (c.m === month && set.has(c.u)) { const b = g(c.u); b.core += c.core; if (c.pen != null) { b.penSum += c.pen; b.penN += 1; } b.penAmt += c.pen_amt; b.net += c.net; b.share += c.share; b.hasComm = true; } });
  const remaining = data.wd[month]?.remaining || 0;
  return Object.entries(by).map(([u, b]) => {
    const a = data.agents[u] || {};
    return {
      u, name: a.name || u, region: a.region || "—", unmatched: u.startsWith("~"),
      target: b.t || null, n: b.n, nTP: b.nTP, att: b.t ? (100 * b.nTP) / b.t : null,
      deficit: b.t ? Math.max(b.t - b.nTP, 0) : null, req: b.t && remaining > 0 ? Math.max(b.t - b.nTP, 0) / remaining : null,
      churned: b.churned, churnPct: b.n + b.churned ? (100 * b.churned) / (b.n + b.churned) : null,
      core: b.hasComm ? b.core : null, pen: b.penN ? b.penSum / b.penN : null, penAmt: b.hasComm ? b.penAmt : null, net: b.hasComm ? b.net : null, share: b.hasComm ? b.share : null,
    };
  }).filter((r) => r.target || r.n || r.core != null);
}

const wLabel = (d) => {
  const day = Number(d.slice(8, 10));
  const w = day <= 7 ? 1 : day <= 14 ? 2 : day <= 21 ? 3 : day <= 28 ? 4 : 5;
  const mon = new Date(d + "T00:00:00").toLocaleString("en", { month: "short" });
  return { key: `${d.slice(0, 7)}-W${w}`, label: `${mon} W${w}` };
};
const mLabelOf = (m) => new Date(m + "-01T00:00:00").toLocaleString("en", { month: "short", year: "2-digit" });

export function trendSeries(data, { mode, set }) {
  const buckets = {};
  const bucket = (d) => {
    const p = mode === "w" ? wLabel(d) : { key: d.slice(0, 7), label: mLabelOf(d.slice(0, 7)) };
    return (buckets[p.key] = buckets[p.key] || { key: p.key, label: p.label, n: 0, churned: 0 });
  };
  data.sales.forEach((s) => { if (set.has(s.u)) bucket(s.d).n += s.n; });
  Object.entries(data.churn_list).forEach(([u, lst]) => { if (set.has(u)) lst.forEach((c) => { bucket(c.d).churned += 1; }); });
  return Object.values(buckets).sort((a, b) => (a.key < b.key ? -1 : 1))
    .map((b) => ({ ...b, churnPct: b.n + b.churned ? (100 * b.churned) / (b.n + b.churned) : null }));
}

export function sortRows(rows, key, dir) {
  return [...rows].sort((a, b) => {
    const av = a[key], bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "string") return dir * av.localeCompare(bv);
    return dir * (av - bv);
  });
}

export const covLabel = (d) => {
  if (!d) return "no data";
  const [, m, day] = d.split("-").map(Number);
  return `${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1]} ${day}`;
};

export const OS_METRICS = [
  { k: "n", label: "Counted Sales", int: true },
  { k: "churned", label: "Churn-flagged sales", int: true },
  { k: "churnPct", label: "Churn-flagged %", pct: true },
];

/* ── document-driven model ─────────────────────────────────────────────── */
export const SHARE = 0.75; // historical; commission is now shown as Core Commission straight from the dump
export const LOC_OF = (c, data) => c.loc || data.agents[c.u]?.region || "";
export const weekOf = (d) => { const day = Number(d.slice(8, 10)); return day <= 7 ? 1 : day <= 14 ? 2 : day <= 21 ? 3 : day <= 28 ? 4 : 5; };
export const WEEK_RANGE = { 1: "1-7", 2: "8-14", 3: "15-21", 4: "22-28", 5: "29-end" };
export const mShort = (m) => (m ? new Date(m + "-01T00:00:00").toLocaleString("en", { month: "short", year: "numeric" }) : "All months");
export const pctChange = (a, b) => (a ? (100 * (b - a)) / a : null);
export const regionSet = (data, r) => new Set(Object.keys(data.agents).filter((u) => r === "Overall" || r === "All" || data.agents[u].region === r));
export const salesIn = (data, { month, set, product, week }) => data.sales.filter((s) => (!month || s.d.startsWith(month)) && set.has(s.u) && (!product || s.p === product) && (!week || weekOf(s.d) === week));
export const churnIn = (data, { month, set, product, week }) => Object.entries(data.churn_list).flatMap(([u, l]) => (set.has(u) ? l.filter((c) => (!month || c.d.startsWith(month)) && (!product || c.p === product) && (!week || weekOf(c.d) === week)).map((c) => ({ ...c, u })) : []));
const targetsIn = (data, f) => data.targets.filter((t) => (!f.month || t.m === f.month) && f.set.has(t.u) && (!f.product || t.p === f.product));
// commission rows are scoped by the dump's own Location column (f.region) when given, otherwise by the agent set
const commIn = (data, f) => (f.week ? [] : data.commission.filter((c) => (!f.month || c.m === f.month) && (f.region ? f.region === "Overall" || f.region === "All" || LOC_OF(c, data) === f.region : f.set.has(c.u))));
// achievement counts an agent's sales only in products where THAT agent has a target
const targetMap = (T) => { const m = {}; T.forEach((t) => (m[t.u] = m[t.u] || new Set()).add(t.p)); return m; };

export function agg(data, f) {
  const S = salesIn(data, f), Ch = churnIn(data, f), T = targetsIn(data, f), Co = commIn(data, f);
  const n = S.reduce((a, s) => a + s.n, 0), churned = Ch.length;
  const tm = targetMap(T);
  const target = T.reduce((a, t) => a + t.t, 0);
  const ach = S.filter((s) => tm[s.u]?.has(s.p)).reduce((a, s) => a + s.n, 0);
  const core = Co.reduce((a, c) => a + c.core, 0), penAmt = Co.reduce((a, c) => a + c.pen_amt, 0), share = Co.reduce((a, c) => a + c.share, 0), net = Co.reduce((a, c) => a + c.net, 0);
  const comm = core;
  const prodCount = {}, typeCount = {};
  S.forEach((s) => { prodCount[s.p] = (prodCount[s.p] || 0) + s.n; typeCount[s.st || "Other"] = (typeCount[s.st || "Other"] || 0) + s.n; });
  const top = Object.entries(prodCount).sort((a, b) => b[1] - a[1])[0];
  return { n, churned, churnPct: n + churned ? (100 * churned) / (n + churned) : null, target, ach, att: target ? (100 * ach) / target : null,
    core, comm, penAmt, penPct: core ? (100 * penAmt) / core : null, net, share, agents: new Set(S.map((s) => s.u)).size, topProduct: top ? { p: top[0], n: top[1] } : null, prodCount, typeCount, commAgents: Co.length };
}

export function agentAgg(data, f) {
  const rows = {};
  const g = (u) => (rows[u] = rows[u] || { u, name: data.agents[u]?.name || u, region: data.agents[u]?.region || "—", n: 0, churned: 0, target: 0, ach: 0, core: 0, penAmt: 0, share: 0, net: 0, hasComm: false, prod: {} });
  const T = targetsIn(data, f), tm = targetMap(T);
  salesIn(data, f).forEach((s) => { const r = g(s.u); r.n += s.n; r.prod[s.p] = (r.prod[s.p] || 0) + s.n; if (tm[s.u]?.has(s.p)) r.ach += s.n; });
  churnIn(data, f).forEach((c) => { g(c.u).churned += 1; });
  T.forEach((t) => { g(t.u).target += t.t; });
  commIn(data, f).forEach((c) => { const r = g(c.u); r.core += c.core; r.penAmt += c.pen_amt; r.share += c.share; r.net += c.net; r.hasComm = true; r.commRows = (r.commRows || 0) + 1; r.penDump = c.pen; if (c.loc) r.region = c.loc; });
  return Object.values(rows).map((r) => {
    const top = Object.entries(r.prod).sort((a, b) => b[1] - a[1])[0];
    return { ...r, churnPct: r.n + r.churned ? (100 * r.churned) / (r.n + r.churned) : null, att: r.target ? (100 * r.ach) / r.target : null,
      comm: r.core, penPct: r.commRows === 1 && r.penDump != null ? r.penDump : r.core ? (100 * r.penAmt) / r.core : null, topProduct: top ? { p: top[0], n: top[1] } : null };
  });
}
export const Arrow = ({ v, lowerBetter = false, suffix = "%", d = 1 }) => {
  if (v == null || isNaN(v)) return <span className="font-mono-hud text-thdim">—</span>;
  const good = lowerBetter ? v <= 0 : v >= 0;
  return <span className="font-mono-hud text-[10px] font-semibold" style={{ color: Math.abs(v) < 0.05 ? C.grey : good ? C.green : C.red }}>{v > 0 ? "▲" : v < 0 ? "▼" : "•"} {Math.abs(v).toFixed(d)}{suffix}</span>;
};
export const Chip = ({ v, lowerBetter = false, suffix = "%", d = 1 }) => {
  if (v == null || isNaN(v)) return null;
  const good = lowerBetter ? v <= 0 : v >= 0, flat = Math.abs(v) < 0.05;
  return <span className="ml-1 rounded-full px-1.5 py-px font-mono-hud text-[9px] font-semibold" style={{ background: `${flat ? C.grey : good ? C.green : C.red}22`, color: flat ? C.grey : good ? C.green : C.red }}>{v > 0 ? "+" : ""}{v.toFixed(d)}{suffix}</span>;
};
export const useSort = (initKey, initDir = -1) => {
  const [s, setS] = useState({ key: initKey, dir: initDir });
  const toggle = (key) => setS((p) => ({ key, dir: p.key === key ? -p.dir : -1 }));
  return [s, toggle];
};
export const Th = ({ k, sort, onSort, children, right = true }) => (
  <th className={`${right ? "text-right" : ""} cursor-pointer select-none whitespace-nowrap hover:text-thstrong`} onClick={() => onSort(k)} data-testid={`os-sort-${k}`}>{children}{sort.key === k ? (sort.dir < 0 ? " ▾" : " ▴") : ""}</th>
);
export const PALETTE = ["#3B82F6", "#2DD4BF", "#F59E0B", "#F43F5E", "#8B5CF6", "#10B981", "#EC4899", "#64748b", "#0EA5E9", "#A3E635", "#F97316"];
